import {
  formatNegotiationDeadlineSlideLabel,
} from '@/utils/negotiationDeadline'
import { formatCurrency } from '@/utils/format-currency'
import {
  formatSupportHoursSlideLabel,
  resolveSupportHoursForSlide,
  resolveSupportTierSlideLines,
} from '@/utils/planSuportePricing'
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { SlidePreview } from './SlidePreview'
import { RocketStandardSlidesGallery } from './RocketStandardSlidesGallery'
import { LayoutSliders } from './LayoutSliders'
import type { useProposalStudio } from './useProposalStudio'

type Studio = ReturnType<typeof useProposalStudio>

export function ProposalStudioPreviewColumn({ s }: { s: Studio }) {
  const [layoutOpen, setLayoutOpen] = useState(false)
  return (
                <div className="w-[58%] overflow-y-auto premium-scroll p-5 space-y-4 flex flex-col items-center">
                  <RocketStandardSlidesGallery />

                  <div className="w-full flex flex-col bg-[var(--glass-bg)] rounded-xl p-4 border border-[var(--glass-border)]">
                    <h4 className="text-xs font-bold text-accent uppercase tracking-wider mb-2">
                      Pré-visualização do Slide (16:9 Widescreen)
                    </h4>
                    <div className="bg-[var(--brand-primary-muted)] border border-[var(--glass-border)] rounded-lg p-2 text-[10px] text-[var(--text-muted)] mb-3">
                      <strong className="text-[var(--text-primary)]">Dica:</strong> arraste os blocos no preview — linhas verdes indicam alinhamento magnético (topo/lateral com outros blocos e colunas). Use os números para falar com o assistente ou copie o template abaixo.
                    </div>

                    {s.activePlan ? (
                      <SlidePreview
                        layout={s.layout}
                        onLayoutChange={s.setLayout}
                        planName={s.activePlan.descricao || s.activePlan.nome || ''}
                        planDescription={s.activePlan.description || ''}
                        supportHoursLabel={formatSupportHoursSlideLabel(
                          resolveSupportHoursForSlide(s.activePlan, s.catalogPlans),
                        )}
                        supportTierLines={resolveSupportTierSlideLines(
                          s.activePlan,
                          s.catalogPlans,
                        )}
                        planTipo={s.activePlan.tipo || 'plano'}
                        projectType={s.activePlan.projectType}
                        planValue={
                          s.showPricing
                            ? s.activePlan.pricing?.installmentValue ?? s.activePlan.valorUnitario ?? 0
                            : 0
                        }
                        isRecurring={true}
                        hidePricing={!s.showPricing}
                        priceCompareFrom={s.showPricing ? s.comboPriceFrom : undefined}
                        paymentDetails={
                          s.showPricing && !s.activePlan.pricing?.recorrente
                            ? `${s.activePlan.pricing?.installments || 1}x de ${formatCurrency(s.activePlan.pricing?.installmentValue || s.activePlan.valorUnitario)}`
                            : ''
                        }
                        loyaltyDuration={
                          s.showPricing
                            ? s.activePlan.pricing?.recorrente
                              ? `Fidelidade: ${s.activePlan.duration || 12} meses`
                              : `Duração: ${s.activePlan.duration || 1} meses`
                            : ''
                        }
                        negotiationDeadlineLabel={
                          s.showPricing
                            ? formatNegotiationDeadlineSlideLabel(
                                s.activePlan.negotiationValidUntil,
                              )
                            : ''
                        }
                        bonusText={s.showPricing && s.activePlan.showBonus === true ? s.activePlan.bonus : ''}
                        serviceGroups={s.activeServiceGroups}
                      />
                    ) : (
                      <div className="w-full aspect-[16/9] flex items-center justify-center bg-[var(--glass-bg)] rounded border border-[var(--glass-border)] text-text-muted">
                        Sem preview disponível.
                      </div>
                    )}
                  </div>

                  {/* Sliders directly positioned underneath mockup */}
                  <div className="w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4 space-y-3">
                    <button
                      type="button"
                      aria-expanded={layoutOpen}
                      onClick={() => setLayoutOpen((current) => !current)}
                      className="flex w-full items-center justify-between gap-3 text-left"
                    >
                      <h4 className="text-xs font-bold uppercase tracking-wider text-accent">
                        Controles de layout
                      </h4>
                      <ChevronDown
                        size={16}
                        className={`text-text-muted transition-transform ${layoutOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {layoutOpen && (
                      <>
                        {s.activePlan?.catalogPlanId && s.comboVariations.length === 0 && (
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="text-[10px] h-7"
                              onClick={() => void s.handleSaveLayoutAsPlanDefault()}
                            >
                              Salvar layout como padrão do plano
                            </Button>
                          </div>
                        )}
                        <LayoutSliders
                          layout={s.layout}
                          readonly={false}
                          categoryNames={s.activeCategoryNames}
                          onSliderChange={s.handleSliderChange}
                          onCategoryCoordChange={s.handleCategoryCoordChange}
                        />
                      </>
                    )}
                  </div>
                </div>
  )
}
